import { Controller } from "@hotwired/stimulus"

// Wraps a Cloudflare Turnstile widget rendered with data-execution="execute".
// Intercepts the enclosing form's submit, calls turnstile.execute() to
// generate a fresh token, and re-submits the form once the success callback
// fires (which populates the cf-turnstile-response hidden input).
//
// Why explicit execute instead of auto-render? Auto-rendered tokens have a
// ~4.5-minute lifetime and can be stale by the time a user finishes typing
// their password. An expired token causes "Couldn't verify you're human" on
// submit even though the widget originally succeeded. Generating the token
// AT submit time eliminates the window.
//
// Each widget gets a unique callback id so multiple widgets on a page (or
// across Turbo renders) don't collide.
export default class extends Controller {
  static targets = ["widget"]
  static values = { callbackId: String }

  connect() {
    this.form = this.element.closest("form")
    if (!this.form) return

    this.boundSubmit = this.onSubmit.bind(this)
    this.form.addEventListener("submit", this.boundSubmit, { capture: true })

    // Cloudflare's script invokes these by global name from the
    // data-callback / data-error-callback / data-expired-callback attrs.
    window[`turnstileSuccess_${this.callbackIdValue}`] = (token) => this.onTokenReady(token)
    window[`turnstileError_${this.callbackIdValue}`] = () => this.onError()
    window[`turnstileExpired_${this.callbackIdValue}`] = () => this.onError()
  }

  disconnect() {
    if (this.form && this.boundSubmit) {
      this.form.removeEventListener("submit", this.boundSubmit, { capture: true })
    }
    delete window[`turnstileSuccess_${this.callbackIdValue}`]
    delete window[`turnstileError_${this.callbackIdValue}`]
    delete window[`turnstileExpired_${this.callbackIdValue}`]
  }

  onSubmit(event) {
    // The token arrived and we triggered a programmatic re-submit — let it pass.
    if (this.programmaticSubmit) {
      this.programmaticSubmit = false
      return
    }

    event.preventDefault()
    event.stopPropagation()

    // Script not loaded yet (slow network, blocked, ad-blocker, etc.) — fall
    // back gracefully: wait a beat then re-attempt the submit. The server-side
    // verifier fails closed if no token arrives, so the user just gets the
    // standard "couldn't verify" error rather than a silent stuck form.
    if (!window.turnstile || !this.hasWidgetTarget) {
      setTimeout(() => this.safeRequestSubmit(), 300)
      return
    }

    try {
      // Cloudflare's 2024 API revision requires execute() to receive a
      // params object with sitekey + callbacks alongside the container.
      // Pass them explicitly here. The render-time data-callback HTML
      // attributes are not read at execute time.
      window.turnstile.execute(this.widgetTarget, {
        sitekey: this.widgetTarget.getAttribute("data-sitekey"),
        callback: (token) => this.onTokenReady(token),
        "error-callback": (errCode) => this.onError(errCode),
        "expired-callback": () => this.onError("expired")
      })
    } catch (e) {
      // execute() throwing now usually means the widget container is
      // disconnected from the DOM (Turbo morph, fragment swap, etc.).
      // Don't blindly retry — the form likely isn't connected either,
      // which would produce the misleading "Form submission canceled"
      // warning. Log and surface a friendly error.
      console.warn("[turnstile-widget] execute threw:", e)
      this.fallbackSubmit()
    }
  }

  // Submit the form ONLY if it's still connected to the DOM. Otherwise
  // a console "Form submission canceled" warning fires and the user sees
  // a stuck form with no feedback. Returns true if the submit fired,
  // false otherwise.
  safeRequestSubmit() {
    if (!this.form || !this.form.isConnected) {
      console.warn("[turnstile-widget] form is disconnected; cannot submit")
      return false
    }
    this.programmaticSubmit = true
    this.form.requestSubmit()
    return true
  }

  // Last-ditch fallback when the widget can't produce a token and we
  // still want to give the server a chance to respond (it will reject
  // for missing-token and produce a user-visible error).
  fallbackSubmit() {
    setTimeout(() => this.safeRequestSubmit(), 300)
  }

  onTokenReady(token) {
    if (!this.form || !this.form.isConnected) {
      console.warn("[turnstile-widget] token ready but form disconnected")
      return
    }
    // The cf-turnstile-response hidden input is normally populated by
    // Cloudflare's script before our callback fires. When we pass the
    // callback inline via execute(params), we ALSO populate it explicitly
    // here as a belt-and-suspenders measure (some Cloudflare versions
    // populate it only when the callback was registered at render time).
    this.ensureTokenInForm(token)
    this.safeRequestSubmit()
  }

  ensureTokenInForm(token) {
    if (!this.form) return
    let input = this.form.querySelector('input[name="cf-turnstile-response"]')
    if (!input) {
      input = document.createElement("input")
      input.type = "hidden"
      input.name = "cf-turnstile-response"
      this.form.appendChild(input)
    }
    input.value = token
  }

  onError(_errCode) {
    // Reset clears the hidden input so any stale token can't be reused on
    // the next submit; the next user click triggers a fresh execute().
    if (window.turnstile && this.hasWidgetTarget) {
      try { window.turnstile.reset(this.widgetTarget) } catch (e) { /* widget may have been unmounted */ }
    }
  }
}
